import { Component } from '@theme/component';
import { prepareComponents } from '@theme/component-loader';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';
import { scheduleBackgroundTask } from '@theme/loading-scheduler';

/** @type {{ url: string, controller: AbortController, promise: Promise<Document>, foreground: boolean, promote: () => void, cartGeneration: number } | null} */
let pendingProductPage = null;
let quickAddAttempt = 0;
let cartContentGeneration = 0;
/** @type {WeakSet<Event>} */
const handledCartUpdates = new WeakSet();

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  #cacheGeneration = 0;
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#cartUpdateAbortController.signal.aborted) this.#cartUpdateAbortController = new AbortController();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated.
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    this.#cacheGeneration += 1;
    this.#cachedContent.clear();
    // Every connected card receives this same event; invalidate shared HTML once.
    if (handledCartUpdates.has(event)) return;
    handledCartUpdates.add(event);
    cartContentGeneration += 1;
    if (pendingProductPage && !pendingProductPage.foreground) pendingProductPage.controller.abort();
  };

  /** @param {string} url @param {Element} productGrid */
  #cacheProductContent(url, productGrid) {
    this.#cachedContent.delete(url);
    this.#cachedContent.set(url, productGrid);
    while (this.#cachedContent.size > 20) {
      const oldest = this.#cachedContent.keys().next().value;
      if (oldest === undefined) break;
      this.#cachedContent.delete(oldest);
    }
  }

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {import('@theme/variant-picker').default | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    const attempt = ++quickAddAttempt;
    const cartGeneration = cartContentGeneration;
    const currentUrl = this.productPageUrl;
    if (pendingProductPage && pendingProductPage.url !== currentUrl) {
      pendingProductPage.controller.abort();
      pendingProductPage = null;
    }
    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }
    try {
      let productGrid = this.#cachedContent.get(currentUrl);
      if (!productGrid) {
        const cacheGeneration = this.#cacheGeneration;
        const html = await this.fetchProductPage(currentUrl, true);
        if (!html || attempt !== quickAddAttempt || cartGeneration !== cartContentGeneration) return;
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (!gridElement) throw new Error('Missing quick-add product content');
        productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        if (cacheGeneration === this.#cacheGeneration) this.#cacheProductContent(currentUrl, productGrid);
      }
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent, attempt, cartGeneration);
      if (attempt !== quickAddAttempt || cartGeneration !== cartContentGeneration || !this.isConnected) return;
      this.#updateVariantPicker(productGrid);
      this.#openQuickAddModal();
    } catch (error) {
      if (attempt !== quickAddAttempt || (error instanceof DOMException && error.name === 'AbortError')) return;
      this.dispatchEvent(new CustomEvent('quick-add:error', { bubbles: true, detail: { error } }));
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @param {boolean} [foreground] - Whether a click is waiting for this page.
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl, foreground = false) {
    if (!productPageUrl) return null;
    if (
      pendingProductPage?.url === productPageUrl &&
      pendingProductPage.cartGeneration === cartContentGeneration &&
      !pendingProductPage.controller.signal.aborted
    ) {
      if (foreground) {
        pendingProductPage.foreground = true;
        pendingProductPage.promote();
      }
      // The request owner reports the error through quick-add:error.
      return pendingProductPage.promise.catch(() => null);
    }
    if (pendingProductPage?.foreground && !foreground) return null;
    if (!foreground && this.#cachedContent.has(productPageUrl)) return null;
    pendingProductPage?.controller.abort();
    const controller = new AbortController();
    const cacheGeneration = this.#cacheGeneration;
    const cartGeneration = cartContentGeneration;
    const assertCurrent = () => {
      controller.signal.throwIfAborted();
      if (cartGeneration !== cartContentGeneration) {
        throw new DOMException('Quick-add content invalidated by a cart update', 'AbortError');
      }
    };
    this.#abortController = controller;
    const scheduled = scheduleBackgroundTask(async () => {
      const response = await fetch(productPageUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      const responseText = await response.text();
      assertCurrent();
      return responseText;
    }, { signal: controller.signal });
    if (foreground) scheduled.promote();
    const promise = scheduled.promise.then(async (responseText) => {
      assertCurrent();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const productGrid = html.querySelector('[data-product-grid-content]');
      if (!productGrid) throw new Error('Missing quick-add product content');
      await prepareComponents(productGrid);
      assertCurrent();
      if (this.isConnected && cacheGeneration === this.#cacheGeneration) {
        this.#cacheProductContent(productPageUrl, /** @type {Element} */ (productGrid.cloneNode(true)));
      }
      return html;
    });
    pendingProductPage = { url: productPageUrl, controller, promise, foreground, promote: scheduled.promote, cartGeneration };
    try {
      return await promise;
    } catch (error) {
      if (!controller.signal.aborted && !(error instanceof DOMException && error.name === 'AbortError')) {
        this.dispatchEvent(new CustomEvent('quick-add:error', { bubbles: true, detail: { error } }));
      }
      return null;
    } finally {
      if (pendingProductPage?.controller === controller) pendingProductPage = null;
      if (this.#abortController === controller) this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   * @param {number} [attempt] - The active modal request.
   * @param {number} [cartGeneration] - The cart content version at the start of the click.
   */
  async updateQuickAddModal(productGrid, attempt = quickAddAttempt, cartGeneration = cartContentGeneration) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      // Keep app scripts and embedded payloads when the mobile layout moves the details.
      for (const script of productDetails?.querySelectorAll('script') ?? []) productGrid.appendChild(script);
      productDetails?.remove();
    }

    // Register optional components before insertion and the initial variant event.
    await prepareComponents(productGrid);
    if (attempt !== quickAddAttempt || cartGeneration !== cartContentGeneration || !this.isConnected) return;

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    if ('directCheckout' in event && event.directCheckout === true) return;
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError || (detail && 'directCheckout' in detail && detail.directCheckout === true)) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') this.dispatchEvent(new CustomEvent('quick-add:error', { bubbles: true, detail: { error } }));
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') this.dispatchEvent(new CustomEvent('quick-add:error', { bubbles: true, detail: { error } }));
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
